/**
 * 文件验证模块
 * 用于校验上传文件的扩展名和 MIME 类型
 * @see Requirements 2.1, 2.4
 */

export interface FileValidationResult {
  valid: boolean;
  error?: string;
  errorCode?: "INVALID_EXTENSION" | "INVALID_MIME_TYPE" | "FILE_TOO_LARGE";
}

export interface FileValidatorOptions {
  allowedExtensions: string[];
  allowedMimeTypes: string[];
  maxFileSizeMB?: number;
}

/** 允许的文件扩展名 */
export const DEFAULT_ALLOWED_EXTENSIONS = [
  "jpg",
  "jpeg",
  "png",
  "heic",
  "heif",
  "webp",
];

/** 允许的 MIME 类型 */
export const DEFAULT_ALLOWED_MIME_TYPES = [
  "image/jpeg",
  "image/png",
  "image/heic",
  "image/heif",
  "image/webp",
];

/**
 * 从文件名中提取扩展名（小写）
 */
export function getFileExtension(filename: string): string {
  const parts = filename.split(".");
  if (parts.length < 2) return "";
  return parts[parts.length - 1].toLowerCase();
}

/**
 * 检查扩展名是否在允许列表中
 */
export function isAllowedExtension(
  extension: string,
  allowed: string[]
): boolean {
  return allowed
    .map((ext) => ext.toLowerCase())
    .includes(extension.toLowerCase());
}

/**
 * 检查 MIME 类型是否在允许列表中
 */
export function isAllowedMimeType(
  mimeType: string,
  allowed: string[]
): boolean {
  // 处理空 MIME 类型（某些浏览器对 HEIC 文件可能返回空）
  if (!mimeType || mimeType === "") {
    return true; // 允许空 MIME 类型，依赖扩展名校验
  }
  return allowed
    .map((mime) => mime.toLowerCase())
    .includes(mimeType.toLowerCase());
}

/**
 * 验证文件是否符合上传要求
 * 同时校验扩展名和 MIME 类型
 */
export function validateFile(
  file: File,
  options?: Partial<FileValidatorOptions>
): FileValidationResult {
  const {
    allowedExtensions = DEFAULT_ALLOWED_EXTENSIONS,
    allowedMimeTypes = DEFAULT_ALLOWED_MIME_TYPES,
    maxFileSizeMB = 15,
  } = options || {};

  // 1. 校验文件大小
  const fileSizeMB = file.size / (1024 * 1024);
  if (fileSizeMB > maxFileSizeMB) {
    return {
      valid: false,
      error: `文件过大，请选择小于 ${maxFileSizeMB}MB 的图片`,
      errorCode: "FILE_TOO_LARGE",
    };
  }

  // 2. 校验扩展名
  const extension = getFileExtension(file.name);
  if (!extension || !isAllowedExtension(extension, allowedExtensions)) {
    return {
      valid: false,
      error: "不支持的文件格式，请上传 JPG、PNG、WebP 或 HEIC 图片",
      errorCode: "INVALID_EXTENSION",
    };
  }

  // 3. 校验 MIME 类型（HEIC 文件可能没有正确的 MIME 类型）
  const isHeicFile = ["heic", "heif"].includes(extension);
  if (!isHeicFile && !isAllowedMimeType(file.type, allowedMimeTypes)) {
    return {
      valid: false,
      error: "文件类型无效，请上传有效的图片文件",
      errorCode: "INVALID_MIME_TYPE",
    };
  }

  return { valid: true };
}
